import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Phone, ShieldCheck, Pencil, X } from 'lucide-react';
import { Button } from './ui/button';
import { API } from '../App';
import { useCredits } from '../context/CreditContext';
import { useSiteTexts } from '../context/SiteTextsContext';

/**
 * Two-step OTP-gated name editor used inside the certificate preview.
 *
 *  Step 1 (`phase: 'phone'`)  — collect / confirm a Rwanda phone
 *  Step 2 (`phase: 'code'`)   — verify the 6-digit SMS code → mint a 24h
 *                              `verification_token` cached in sessionStorage
 *  Step 3 (`phase: 'name'`)   — edit the name; PUT /me/profile-name with
 *                              the cached token. Skipped entirely on
 *                              subsequent edits within the 24h window.
 *
 * The component is purposely self-contained — drop it into any cert
 * preview and it will refresh the parent's `user` (via CreditContext)
 * so the new name flows everywhere (Nav avatar, future certs, etc).
 */
const TOKEN_KEY = 'ikz_cert_verification_token';
const TOKEN_EXPIRY_KEY = 'ikz_cert_verification_expiry';

const cachedToken = () => {
  const t = sessionStorage.getItem(TOKEN_KEY);
  const exp = parseInt(sessionStorage.getItem(TOKEN_EXPIRY_KEY) || '0', 10);
  if (!t || !exp || Date.now() > exp) return null;
  return t;
};

const cacheToken = (token, expiresInSec) => {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + (expiresInSec || 86400) * 1000));
};

export default function CertNameEditor({ isDark, onClose, onSaved }) {
  const { user, refreshUser } = useCredits();
  const { getText } = useSiteTexts();

  // If we already have a valid token, skip the OTP steps entirely.
  const initialPhase = cachedToken() ? 'name' : 'phone';

  const [phase, setPhase] = useState(initialPhase);
  const [phone, setPhone] = useState(user?.phone || '');
  const [code, setCode] = useState('');
  const [name, setName] = useState(user?.name || '');
  const [busy, setBusy] = useState(false);

  const t = (key, fallback) => getText(key, 'certificates') || fallback;

  const inputBase = `w-full rounded-[10px] border px-3 py-2 text-[14px] outline-none focus:ring-2 focus:ring-brand-500/40 ${
    isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-[#0e1f35]'
  }`;

  const sendOtp = async () => {
    if (!phone.trim()) {
      toast.error('Enter a phone number');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/me/phone/send-otp`, { phone }, { withCredentials: true });
      if (res.data?.status === 'cooldown') {
        toast.info(res.data.message || 'Please wait before requesting another code');
      } else {
        toast.success('Code sent — check your messages');
      }
      setPhase('code');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send code');
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (code.length < 4) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/me/phone/verify-otp`, { phone, code }, { withCredentials: true });
      cacheToken(res.data.verification_token, res.data.expires_in);
      toast.success('Phone verified');
      setPhase('name');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const saveName = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    const token = cachedToken();
    if (!token) {
      // Token expired between mount and save — restart OTP.
      toast.error('Verification expired — please verify your phone again');
      setPhase('phone');
      return;
    }
    setBusy(true);
    try {
      await axios.put(`${API}/me/profile-name`, { name: trimmed, verification_token: token }, { withCredentials: true });
      await refreshUser?.();
      toast.success('Name updated on your certificates');
      onSaved?.(trimmed);
      onClose?.();
    } catch (err) {
      if (err.response?.status === 401) {
        // Token rejected server-side — purge cache and restart OTP.
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
        setPhase('phone');
        toast.error('Verification expired — please verify again');
      } else {
        toast.error(err.response?.data?.detail || 'Could not save name');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="cert-name-editor-overlay"
    >
      <div
        className={`relative w-full max-w-md rounded-2xl border ${
          isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
        } p-6 shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
        data-testid="cert-name-editor"
      >
        <button
          onClick={onClose}
          className={`absolute top-3 right-3 p-1 rounded-md ${
            isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
          }`}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-1">
          {phase === 'phone' && <Phone className="w-5 h-5 text-brand-500" />}
          {phase === 'code' && <ShieldCheck className="w-5 h-5 text-brand-500" />}
          {phase === 'name' && <Pencil className="w-5 h-5 text-brand-500" />}
          <h3 className={`text-[16px] font-semibold ${isDark ? 'text-white' : 'text-[#0e1f35]'}`}>
            {t('cert_edit_name_title', 'Set the name on your certificate')}
          </h3>
        </div>
        <p className={`text-[12px] mb-4 ${isDark ? 'text-gray-400' : 'text-[#4a6078]'}`}>
          {t('cert_edit_name_desc', "We'll verify your phone first so only you can change the name on your certificates.")}
        </p>

        {phase === 'phone' && (
          <>
            <label className={`block text-[12px] font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-[#0e1f35]'}`}>
              {t('cert_otp_phone_label', 'Phone number (+250)')}
            </label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+250 78x xxx xxx"
              className={inputBase}
              data-testid="cert-otp-phone-input"
              autoFocus
            />
            <Button
              onClick={sendOtp}
              disabled={busy}
              className="w-full mt-4 bg-brand-500 hover:bg-brand-600 text-white"
              data-testid="cert-otp-send-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('cert_otp_send_btn', 'Send code')}
            </Button>
          </>
        )}

        {phase === 'code' && (
          <>
            <label className={`block text-[12px] font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-[#0e1f35]'}`}>
              {t('cert_otp_code_label', '6-digit code')}
            </label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              maxLength={6}
              className={`${inputBase} text-center tracking-[0.4em] font-mono text-[18px]`}
              data-testid="cert-otp-code-input"
              autoFocus
            />
            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setPhase('phone')}
                disabled={busy}
                className="flex-1"
                data-testid="cert-otp-back-btn"
              >
                Back
              </Button>
              <Button
                onClick={verifyOtp}
                disabled={busy || code.length < 4}
                className="flex-1 bg-brand-500 hover:bg-brand-600 text-white"
                data-testid="cert-otp-verify-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('cert_otp_verify_btn', 'Verify')}
              </Button>
            </div>
          </>
        )}

        {phase === 'name' && (
          <>
            <label className={`block text-[12px] font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-[#0e1f35]'}`}>
              {t('cert_name_label', 'Name to appear on certificate')}
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className={inputBase}
              data-testid="cert-name-input"
              autoFocus
            />
            <p className={`text-[11px] mt-1 ${isDark ? 'text-gray-500' : 'text-[#4a6078]/80'}`}>
              This updates your profile and every certificate you've issued.
            </p>
            <Button
              onClick={saveName}
              disabled={busy}
              className="w-full mt-4 bg-brand-500 hover:bg-brand-600 text-white"
              data-testid="cert-save-name-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('cert_save_name_btn', 'Save name')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
